import { ErrorHandler, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { LandingSurface } from '../models/landing-surface.model';
import { TakeoffLandArea } from '../models/takeoff-land-area.model';
import { Obstacle } from '../models/obstacle.model';
import { ApproachLighting } from '../models/approach-lighting.model';

@Injectable()
export class RunwayService {

  constructor(private _http: HttpClient,
              private _errorHandler: ErrorHandler) { }

  /**
   * updates specified entity via api
   * @param {LandingSurface} runway
   * @return {Promise<any>}
   */
  putRunway(airportId: number, runway: LandingSurface): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportId}/landingSurface/${runway.landingSurfaceId}`;
    try {
      const BODY = this._getRunwayJSON(runway);
      return this._http.put(URL, BODY).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * adds specified entity via api
   * @param {LandingSurface} runway
   * @return {Promise<any>}
   */
  postRunway(airportId: number, runway: LandingSurface): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportId}/landingSurface/`;
    try {
      const BODY = this._getRunwayJSON(runway);
      return this._http.post(URL, BODY).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * deletes the specified entity via api
   * @param {LandingSurface} runway
   * @return {Promise<any>}
   */
  deleteRunway(airportId: number, runwayId: number): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportId}/landingSurface/${runwayId}`;
    try {
      return this._http.delete(URL).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * updates specified entity via api
   * @param {TakeoffLandArea} takeoffLandArea
   * @return {Promise<any>}
   */
  putTakeoffLandArea(airportId: number, takeoffLandArea: TakeoffLandArea): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportId}/takeoffLandArea/${takeoffLandArea.takeoffLandAreaId}`;
    try {
      const BODY = this._getTakeoffLandAreaJSON(takeoffLandArea);
      return this._http.put(URL, BODY).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * adds specified entity via api
   * @param {TakeoffLandArea} takeoffLandArea
   * @return {Promise<any>}
   */
  postTakeoffLandArea(airportId: number, takeoffLandArea: TakeoffLandArea, runwayId: number): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportId}/takeoffLandArea/${runwayId}`;
    try {
      const BODY = this._getTakeoffLandAreaJSON(takeoffLandArea);
      return this._http.post(URL, BODY).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * deletes the specified entity via api
   * @param {TakeoffLandArea} takeoffLandArea
   * @return {Promise<any>}
   */
  deleteTakeoffLandArea(airportId: number, takeoffLandAreaId: number): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportId}/takeoffLandArea/${takeoffLandAreaId}`;
    try {
      return this._http.delete(URL).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * updates specified entity via api
   * @param {Obstacle} obstacle
   * @return {Promise<any>}
   */
  putObstacle(airportId: number, obstacle: Obstacle): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportId}/obstacle/${obstacle.obstacleId}`;
    try {
      const BODY = this._getObstacleJSON(obstacle);
      return this._http.put(URL, BODY).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * adds specified entity via api
   * @param {Obstacle} obstacle
   * @return {Promise<any>}
   */
  postObstacle(airportId: number, obstacle: Obstacle): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportId}/obstacle/${obstacle.takeoffLandAreaId}`;
    try {
      const BODY = this._getObstacleJSON(obstacle);
      return this._http.post(URL, BODY).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * deletes the specified entity via api
   * @param {Obstacle} obstacle
   * @return {Promise<any>}
   */
  deleteObstacle(airportId: number, obstacleId: number): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportId}/obstacle/${obstacleId}`;
    try {
      return this._http.delete(URL, { headers: {'Content-Type': 'application/json'} }).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * updates specified entity via api
   * @param {ApproachLighting} lighting
   * @return {Promise<any>}
   */
  putLighting(airportId: number, lighting: ApproachLighting): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportId}/lighting/${lighting.lightingId}`;
    try {
      const BODY = this._getLightingJSON(lighting);
      return this._http.put(URL, BODY).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * adds specified entity via api
   * @param {ApproachLighting} lighting
   * @return {Promise<any>}
   */
  postLighting(airportId: number, lighting: ApproachLighting): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportId}/lighting/${lighting.takeoffLandAreaId}`;
    try {
      const BODY = this._getLightingJSON(lighting);
      return this._http.post(URL, BODY).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * deletes the specified entity via api
   * @param {ApproachLighting} lighting
   * @return {Promise<any>}
   */
  deleteLighting(airportId: number, lightingId: number): Promise<any> {
    const URL = `${environment.airportsApiUrl}/airports/${airportId}/lighting/${lightingId}`;
    try {
      return this._http.delete(URL, { headers: {'Content-Type': 'application/json'} }).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * converts entity to json obj
   * @param {LandingSurface} runway
   * @return {any}
   * @private
   */
  _getRunwayJSON(runway: LandingSurface): any {
    return {
      landingSurfaceId: runway.landingSurfaceId,
      name: runway.name,
      length: runway.length,
      width: runway.width,
      areaType: runway.type != null ? LandingSurface.formatLandingSurfaceType(runway.type).replace('helipad', 'pad') : null,
      materialType1: runway.materialType1,
      materialType2: runway.materialType2,
      treatment: runway.treatment,
      condition: LandingSurface.formatCondition(runway.condition),
      pavementType: LandingSurface.formatPavementType(runway.pavementType),
      edgeLighting: LandingSurface.formatEdgeLighting(runway.edgeLighting),
      pcn: runway.pcn,
      subgradeStrength: runway.subgradeStrength,
      tirePressure: runway.tirePressure,
      wtBearing1Wheel: runway.wtBearing1Wheel,
      wtBearing2Wheel: runway.wtBearing2Wheel,
      wtBearing2WheelDblTandem: runway.wtBearing2WheelTandem,
      wtBearing2WheelTandem: runway.wtBearing2WheelTandem
    };
  }

  /**
   * converts entity to json obj
   * @param {TakeoffLandArea} takeoffLandArea
   * @return {any}
   * @private
   */
  _getTakeoffLandAreaJSON(takeoffLandArea: TakeoffLandArea): any {
    return {
      takeoffLandAreaId: takeoffLandArea.takeoffLandAreaId,
      tlaName: takeoffLandArea.name,
      latitude: takeoffLandArea.location.lat,
      longitude: takeoffLandArea.location.lon,
      trafficPatDirFixed: TakeoffLandArea.formatTrafficPatternDirection(takeoffLandArea.trafficPatDirFixed),
      elev: takeoffLandArea.elevation,
      trueAlignment: takeoffLandArea.trueAlignment,
      markingsType: takeoffLandArea.markingsType,
      markingsCondition: TakeoffLandArea.formatRunwayMarkingsCondition(takeoffLandArea.markingsCondition),
      ilsType: TakeoffLandArea.formatILSType(takeoffLandArea.ilsType),
      gradient: takeoffLandArea.gradient
    };
  }

  _getObstacleJSON(obstacle: Obstacle): any {
    return {
      obstacleId: obstacle.obstacleId,
      takeoffLandAreaId: obstacle.takeoffLandAreaId,
      heightAgl: obstacle.heightAgl,
      obstacleType: obstacle.obstacleType,
      distanceFromEnd: obstacle.distanceFromEnd,
      centerlineOffset: obstacle.centerlineOffset,
      offsetDirection: obstacle.offsetDirection,
      clearanceSlope: obstacle.clearanceSlope,
      marking: obstacle.marking
    };
  }

  _getLightingJSON(lighting: ApproachLighting): any {
    return {
      lightingId: lighting.lightingId,
      takeoffLandAreaId: lighting.takeoffLandAreaId,
      lightingType: ApproachLighting.formatApproachLightingType(lighting.type),
      description: lighting.description
    };
  }
}

